import React from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Loader2, FileText, X } from "lucide-react";
import { fmtDateEST, fmtEST } from "../../utils/dateUtils";

// Activity Report Components (private to this file)
const DashboardReport = ({ data }) => {
  if (!data || !data.summary) return null;

  const { summary, user, date_range } = data;

  return (
    <div className="space-y-4 print:p-4">
      <div className="bg-slate-50 p-4 rounded-lg print:bg-white">
        <h4 className="font-semibold text-sm text-slate-600 mb-2">Report Summary</h4>
        <p className="text-sm">User: <span className="font-medium">{user?.full_name}</span></p>
        <p className="text-sm">Period: <span className="font-medium">{date_range?.label}</span></p>
        <p className="text-sm">
          {fmtDateEST(date_range?.start)} - {fmtDateEST(date_range?.end)}
        </p>
      </div>

      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-blue-700">{summary.total_activities}</p>
            <p className="text-sm text-blue-600 mt-1">Total Activities</p>
          </CardContent>
        </Card>

        {Object.entries(summary.by_action || {}).slice(0, 3).map(([action, count]) => (
          <Card key={action} className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="pt-6 text-center">
              <p className="text-2xl font-bold text-purple-700">{count}</p>
              <p className="text-xs text-purple-600 mt-1">{action.replace(/_/g, ' ').toUpperCase()}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Activities by Type</CardTitle>
          </CardHeader>
          <CardContent>
            {Object.entries(summary.by_action || {}).map(([action, count]) => (
              <div key={action} className="flex items-center justify-between py-2 border-b last:border-0">
                <span className="text-sm">{action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                <Badge variant="outline" className="bg-purple-50 text-purple-700">{count}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Activity Timeline</CardTitle>
          </CardHeader>
          <CardContent>
            {Object.entries(summary.by_date || {}).sort().reverse().map(([date, count]) => (
              <div key={date} className="flex items-center justify-between py-2 border-b last:border-0">
                <span className="text-sm">{fmtDateEST(date)}</span>
                <Badge variant="outline" className="bg-blue-50 text-blue-700">{count}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Button onClick={() => window.print()} className="w-full bg-purple-600 hover:bg-purple-700">
        Print Dashboard
      </Button>
    </div>
  );
};

const DetailReport = ({ data }) => {
  if (!data || !data.activities) return null;

  const { activities, user, date_range } = data;

  return (
    <div className="space-y-4 print:p-4">
      <div className="bg-slate-50 p-4 rounded-lg print:bg-white">
        <h4 className="font-semibold text-sm text-slate-600 mb-2">Detailed Activity Report</h4>
        <p className="text-sm">User: <span className="font-medium">{user?.full_name}</span> ({user?.email})</p>
        <p className="text-sm">Period: {fmtDateEST(date_range?.start)} - {fmtDateEST(date_range?.end)}</p>
        <p className="text-sm">Total Activities: <span className="font-medium">{data.total_activities}</span></p>
      </div>

      {activities.length === 0 ? (
        <p className="text-center text-slate-500 py-8">No activities found for this period</p>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-100">
              <tr>
                <th className="text-left p-3 font-semibold">Date & Time</th>
                <th className="text-left p-3 font-semibold">Action</th>
                <th className="text-left p-3 font-semibold">Entity</th>
                <th className="text-left p-3 font-semibold">Details</th>
              </tr>
            </thead>
            <tbody>
              {activities.map((activity, idx) => (
                <tr key={activity.id || idx} className="border-t hover:bg-slate-50">
                  <td className="p-3">
                    {fmtEST(activity.created_at)}
                  </td>
                  <td className="p-3">
                    <Badge className={
                      activity.action?.includes('created') ? 'bg-green-100 text-green-700' :
                      activity.action?.includes('updated') ? 'bg-blue-100 text-blue-700' :
                      activity.action?.includes('deleted') ? 'bg-red-100 text-red-700' :
                      activity.action?.includes('email') ? 'bg-purple-100 text-purple-700' :
                      activity.action?.includes('sms') ? 'bg-cyan-100 text-cyan-700' :
                      activity.action?.includes('call') ? 'bg-orange-100 text-orange-700' :
                      'bg-slate-100 text-slate-700'
                    }>
                      {activity.action?.replace(/_/g, ' ').toUpperCase()}
                    </Badge>
                  </td>
                  <td className="p-3 capitalize">{activity.entity_type || '-'}</td>
                  <td className="p-3 text-xs text-slate-600">
                    {activity.changes ? JSON.stringify(activity.changes).substring(0, 100) : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Button onClick={() => window.print()} className="w-full">
        Print Detail Report
      </Button>
    </div>
  );
};

const ActivityReportDialog = React.memo(function ActivityReportDialog({
  open, onOpenChange, activityReportUser, activityDateRange, setActivityDateRange,
  activityCustomStart, setActivityCustomStart, activityCustomEnd, setActivityCustomEnd,
  activityTypes, setActivityTypes, activityReportType, setActivityReportType,
  activityReportData, setActivityReportData, loadingActivityReport, onGenerate
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-purple-600" />
            Activity Report - {activityReportUser?.full_name}
          </DialogTitle>
          <DialogDescription>
            Generate detailed or dashboard view of user activities
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {!activityReportData ? (
            <>
              {/* Configuration Form */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date Range</Label>
                  <Select value={activityDateRange} onValueChange={setActivityDateRange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="today">Today</SelectItem>
                      <SelectItem value="yesterday">Yesterday</SelectItem>
                      <SelectItem value="this_week">This Week</SelectItem>
                      <SelectItem value="last_week">Last Week</SelectItem>
                      <SelectItem value="this_month">This Month</SelectItem>
                      <SelectItem value="last_month">Last Month</SelectItem>
                      <SelectItem value="ytd">Year to Date</SelectItem>
                      <SelectItem value="custom">Custom Dates</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Report Type</Label>
                  <Select value={activityReportType} onValueChange={setActivityReportType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="dashboard">Dashboard (Summary)</SelectItem>
                      <SelectItem value="detail">Detail (Full List)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {activityDateRange === 'custom' && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Date</Label>
                    <Input
                      type="date"
                      value={activityCustomStart}
                      onChange={(e) => setActivityCustomStart(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End Date</Label>
                    <Input
                      type="date"
                      value={activityCustomEnd}
                      onChange={(e) => setActivityCustomEnd(e.target.value)}
                    />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label>Activity Types</Label>
                <div className="grid grid-cols-3 gap-2 p-3 border rounded-lg bg-slate-50">
                  {['all', 'lead_created', 'lead_updated', 'lead_deleted', 'email_sent', 'sms_sent', 'call_logged', 'note_added', 'task_created'].map(type => (
                    <div key={type} className="flex items-center space-x-2">
                      <Checkbox
                        id={`activity-${type}`}
                        checked={activityTypes.includes(type) || activityTypes.includes('all')}
                        onCheckedChange={(checked) => {
                          if (type === 'all') {
                            setActivityTypes(checked ? ['all'] : []);
                          } else {
                            if (checked) {
                              setActivityTypes(prev => prev.filter(t => t !== 'all').concat(type));
                            } else {
                              setActivityTypes(prev => prev.filter(t => t !== type && t !== 'all'));
                            }
                          }
                        }}
                        disabled={activityTypes.includes('all') && type !== 'all'}
                      />
                      <Label htmlFor={`activity-${type}`} className="text-sm font-normal cursor-pointer">
                        {type === 'all' ? 'ALL' : type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              <Button onClick={onGenerate} disabled={loadingActivityReport} className="w-full">
                {loadingActivityReport ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating Report...
                  </>
                ) : (
                  <>
                    <FileText className="h-4 w-4 mr-2" />
                    Generate Activity Report
                  </>
                )}
              </Button>
            </>
          ) : (
            <>
              {/* Report Display */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">
                    {activityReportType === 'dashboard' ? 'Activity Dashboard' : 'Detailed Activity Report'}
                  </h3>
                  <Button variant="outline" size="sm" onClick={() => setActivityReportData(null)}>
                    <X className="h-4 w-4 mr-1" />
                    New Report
                  </Button>
                </div>

                {activityReportType === 'dashboard' ? (
                  <DashboardReport data={activityReportData} />
                ) : (
                  <DetailReport data={activityReportData} />
                )}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
});

export default ActivityReportDialog;
